import React, { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Reusable Modal Component with backdrop click and ESC key support
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal closes
 * @param {string} title - Modal title
 * @param {ReactNode} children - Modal content
 * @param {string} maxWidth - Max width class (default: max-w-2xl)
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-md',
  size = null // 'sm', 'md', 'lg', 'xl', '2xl'
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  // Size presets for consistent modal sizes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    // documents with lines (quote, order, invoice): room for the line columns
    doc: 'max-w-5xl'
  };

  const modalMaxWidth = size ? sizeClasses[size] : maxWidth;
  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // OFF-27: lets the floating assistant button step aside (index.css)
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-surface-950/60 backdrop-blur-sm flex items-start md:items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-white dark:bg-surface-900 rounded-xl2 shadow-pop ring-1 ring-surface-200 dark:ring-surface-800 ${modalMaxWidth} w-full my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
        // F42: announce it as a dialog named by its title (screen readers, tests)
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        {title && (
          <div className="flex justify-between items-center px-5 sm:px-6 py-4 border-b border-surface-200 dark:border-surface-800 sticky top-0 bg-white/95 dark:bg-surface-900/95 backdrop-blur z-10">
            <h2 id={titleId} className="text-lg font-semibold text-surface-900 dark:text-surface-50">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
              aria-label={t('shared.modal.close')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="p-5 sm:p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
